import React, { useRef, useState } from 'react';
import HrnbButton from '../controls/HrnbButton';
import HrnbAlert from '../controls/HrnbAlert';
import { filesApi } from '../../api/files';
import '../../css/components/FileUploader.css';

/**
 * Компонент загрузки файлов
 * @param {Object} props
 * @param {Function} props.onFileUploaded - Callback с данными загруженного файла
 * @param {Function} props.onFileRemoved - Callback при удалении файла из списка
 * @param {Array} props.uploadedFiles - Массив уже загруженных файлов
 * @param {string} props.metadata - Метаданные для загрузки (по умолчанию 'forum_attachment')
 * @param {number} props.maxFileSize - Максимальный размер файла в MB (по умолчанию 5)
 * @param {boolean} props.disabled - Отключить загрузку
 */
export default function FileUploader({
    onFileUploaded,
    onFileRemoved,
    uploadedFiles = [],
    metadata = 'forum_attachment',
    maxFileSize = 5,
    disabled = false,
    multiple = true
}) {
    const fileInputRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState(null);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadState, setUploadState] = useState(''); // "Файл 2 из 5"

    const allowedTypes = [
        // Изображения
        'image/jpeg', 'image/png', 'image/gif', 'image/bmp', 'image/webp', 'image/svg+xml',
        // Видео
        'video/mp4', 'video/avi', 'video/mov', 'video/mkv', 'video/webm', 'video/quicktime',
        // Аудио
        'audio/mp3', 'audio/mpeg', 'audio/wav', 'audio/ogg', 'audio/flac', 'audio/aac', 'audio/x-wav',
        // Документы
        'application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'text/plain', 'text/markdown',
        'application/zip', 'application/x-rar-compressed',
        'application/vnd.ms-powerpoint', 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        'text/csv', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];

    const getFileTypeIcon = (fileType) => {
        if (fileType?.startsWith('image/')) return '🖼️';
        if (fileType?.startsWith('video/')) return '🎬';
        if (fileType?.startsWith('audio/')) return '🎵';
        if (fileType === 'application/pdf') return '📄';
        if (fileType?.includes('word')) return '📝';
        if (fileType?.includes('powerpoint')) return '📊';
        if (fileType?.includes('excel') || fileType === 'text/csv') return '📈';
        if (fileType?.includes('zip') || fileType?.includes('rar')) return '📦';
        return '📎';
    };

    const getFileTypeLabel = (fileType) => {
        if (fileType === 'IMAGE') return 'Изображение';
        if (fileType === 'VIDEO') return 'Видео';
        if (fileType === 'AUDIO') return 'Аудио';
        if (fileType === 'DOCUMENT') return 'Документ';
        if (fileType === 'TEXT') return 'Текст';
        if (fileType === 'ARCHIVE') return 'Архив';
        if (fileType === 'PRESENTATION') return 'Презентация';
        if (fileType === 'SPREADSHEET') return 'Таблица';
        return 'Файл';
    };

    const getFileTypeClass = (fileType) => {
        if (fileType === 'IMAGE') return 'image';
        if (fileType === 'VIDEO') return 'video';
        if (fileType === 'AUDIO') return 'audio';
        return '';
    };

    const validateFile = (file) => {
        const fileSizeBytes = maxFileSize * 1024 * 1024;
        
        if (file.size > fileSizeBytes) {
            return `Файл "${file.name}" слишком большой. Максимальный размер: ${maxFileSize} MB`;
        }

        // Простая валидация по расширению
        const extension = file.name.split('.').pop().toLowerCase();
        const allowedExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg', 
                                    'mp4', 'avi', 'mov', 'mkv', 'webm',
                                    'mp3', 'wav', 'ogg', 'flac', 'aac',
                                    'pdf', 'doc', 'docx', 'txt', 'md',
                                    'zip', 'rar', 'tar', '7z',
                                    'ppt', 'pptx', 'csv', 'xls', 'xlsx'];
        
        if (!allowedExtensions.includes(extension)) {
            return `Файл "${file.name}" имеет неподдерживаемый формат`;
        }

        return null;
    };

    const determineFileType = (file) => {
        if (file.type.startsWith('image/')) return 'IMAGE';
        if (file.type.startsWith('video/')) return 'VIDEO';
        if (file.type.startsWith('audio/')) return 'AUDIO';
        if (file.type.includes('pdf')) return 'DOCUMENT';
        if (file.type.includes('word') || file.type.includes('document')) return 'DOCUMENT';
        if (file.type.includes('text')) return 'TEXT';
        if (file.type.includes('zip') || file.type.includes('rar') || file.type.includes('archive')) return 'ARCHIVE';
        if (file.type.includes('powerpoint') || file.type.includes('presentation')) return 'PRESENTATION';
        if (file.type.includes('excel') || file.type.includes('spreadsheet') || file.type === 'text/csv') return 'SPREADSHEET';
        return metadata;
    };

    const uploadSingleFile = async (file, fileIndex, totalFiles) => {
        setUploadState(`Файл ${fileIndex} из ${totalFiles}`);
        setUploadProgress(0);

        const fileType = determineFileType(file);

        const response = await filesApi.upload(file, { type: fileType }, (progressEvent) => {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(progress);
        });

        const fileData = response.file || response.data;
        if (onFileUploaded) {
            onFileUploaded(fileData);
        }
    };

    const handleFileSelect = async (event) => {
        const files = event.target.files;
        if (!files || files.length === 0) return;

        const fileArray = Array.from(files);
        setError(null);

        // Предварительная валидация всех файлов
        const errors = [];
        const validFiles = [];
        for (const file of fileArray) {
            const validationError = validateFile(file);
            if (validationError) {
                errors.push(validationError);
            } else {
                validFiles.push(file);
            }
        }

        if (errors.length > 0) {
            const errorMsg = errors.length === 1
                ? errors[0]
                : `Некоторые файлы не прошли проверку:\n• ${errors.join('\n• ')}`;
            setError(errorMsg);
            // Если ни один файл не прошёл валидацию — выходим
            if (validFiles.length === 0) {
                fileInputRef.current.value = '';
                return;
            }
        }

        try {
            setUploading(true);
            setUploadProgress(0);

            // Загружаем файлы последовательно
            for (let i = 0; i < validFiles.length; i++) {
                await uploadSingleFile(validFiles[i], i + 1, validFiles.length);
            }

            setUploadState('');

            if (validFiles.length > 1 && errors.length === 0) {
                setUploadProgress(100);
            }

            fileInputRef.current.value = '';
        } catch (err) {
            console.error('[FileUploader] Upload failed:', err);
            // Показываем общую ошибку, но не перетираем ошибки валидации
            if (!errors.length) {
                setError(err.response?.data?.message || err.message || 'Ошибка при загрузке файла');
            }
        } finally {
            setUploading(false);
            setUploadProgress(0);
            setUploadState('');
        }
    };

    const handleRemoveFile = (fileId) => {
        if (onFileRemoved) {
            onFileRemoved(fileId);
        }
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
    };

    const triggerFileInput = () => {
        if (!disabled && !uploading) {
            fileInputRef.current?.click();
        }
    };

    return (
        <div className="file-uploader">
            <input
                ref={fileInputRef}
                type="file"
                multiple={multiple}
                onChange={handleFileSelect}
                className="file-uploader__input"
                disabled={disabled || uploading}
                accept={allowedTypes.join(',')}
            />

            <HrnbButton
                variant="outline"
                onClick={triggerFileInput}
                disabled={disabled || uploading}
                className="file-uploader__button"
            >
                {uploading ? (uploadState || 'Загрузка...') : '📎 Прикрепить файл'}
            </HrnbButton>

            {uploading && uploadProgress > 0 && (
                <div className="file-uploader__progress">
                    <div className="file-uploader__progress-bar">
                        <div 
                            className="file-uploader__progress-fill" 
                            style={{ width: `${uploadProgress}%` }}
                        />
                    </div>
                    <span className="file-uploader__progress-text">
                        {uploadState ? `${uploadState} — ${uploadProgress}%` : `${uploadProgress}%`}
                    </span>
                </div>
            )}

            {error && (
                <HrnbAlert variant="danger" onClose={() => setError(null)} className="mt-2">
                    {error}
                </HrnbAlert>
            )}

            {uploadedFiles && uploadedFiles.length > 0 && (
                <div className="file-uploader__list">
                    {uploadedFiles.map((file) => {
                        const fileTypeClass = getFileTypeClass(file.fileType);
                        return (
                            <div key={file.id} className="file-uploader__item">
                                <span className="file-uploader__item-icon">
                                    {getFileTypeIcon(file.contentType)}
                                </span>
                                <div className="file-uploader__item-info">
                                    <span className="file-uploader__item-name">{file.fileName}</span>
                                    <div className="file-uploader__item-meta">
                                        {file.fileType && (
                                            <span className={`file-uploader__item-type ${fileTypeClass}`}>
                                                {getFileTypeLabel(file.fileType)}
                                            </span>
                                        )}
                                        <span className="file-uploader__item-size">
                                            {formatFileSize(file.fileSize)}
                                        </span>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    className="file-uploader__item-remove"
                                    onClick={() => handleRemoveFile(file.id)}
                                    title="Удалить файл"
                                    disabled={uploading}
                                >
                                    ✕
                                </button>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
